import type { ServicePageContent } from '../types/servicePage'

export const originalServicePages: ServicePageContent[] = [
  {
    slug: 'handyman-services-las-vegas',
    name: 'Handyman Services',
    title: 'Handyman services',
    intro:
      'Residential and commercial handyman services across Las Vegas. From light fixture installs to major renovations, our team has the skills, tools and experience to handle jobs big and small.',
    coverageIntro: 'One call covers a long list of jobs, so you don’t have to juggle multiple contractors.',
    coverage: [
      { title: 'Kitchen & bathroom remodeling', text: 'Updates and remodels for the rooms you use most.' },
      { title: 'Drywall repair & installation', text: 'Holes, cracks and new walls hung and finished.' },
      { title: 'Painting', text: 'Interior and exterior painting for single rooms or whole properties.' },
      { title: 'Flooring', text: 'Removal and installation of new flooring.' },
      { title: 'Carpentry & custom woodwork', text: 'Trim, cabinets, shelving and custom pieces.' },
      { title: 'Commercial property maintenance', text: 'Ongoing repairs and upkeep for business properties.' },
      { title: 'Home renovations', text: 'Refurbishments that bring older spaces up to date.' },
      { title: 'Fixtures & small jobs', text: 'Light fixture installs, shelves, TV mounts and the to-do list in between.' },
    ],
    sections: [
      {
        title: 'For homes and commercial properties',
        body: [
          'Whether you own a home or a commercial property, our team is equipped to handle a wide range of tasks, no matter how big or small. From light fixture installations to significant renovations, we deliver complete handyman solutions.',
        ],
      },
      {
        title: 'Versatility that saves you time',
        body: [
          'Our crew can help with drywall repair, plumbing issues, electrical work, painting and remodeling, to name a few. We’re the jack of all trades you need to keep your property in prime condition.',
        ],
      },
      {
        title: 'Quality and efficiency',
        body: [
          'We know how important it is to have repairs done correctly, on time and within budget. Our handymen work quickly without cutting corners, use top-grade tools and follow industry standards so the work lasts.',
        ],
      },
      {
        title: 'Focused on your satisfaction',
        body: [
          'Our team is known for being friendly and willing to go the extra mile. We take the time to fully understand what you need and tailor the work to match your expectations.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What areas do your handyman services cover?',
        a: 'Our handyman services cover the entire Las Vegas area, including the surrounding suburbs.',
      },
      {
        q: 'Do you work on commercial properties?',
        a: 'Yes. We handle both residential homes and commercial properties, from small repairs to renovations and ongoing maintenance.',
      },
    ],
    related: [
      'drywall-services-las-vegas',
      'painting-services-las-vegas',
      'carpentry-services-las-vegas',
      'flooring-installation-las-vegas',
    ],
  },
  {
    slug: 'drywall-services-las-vegas',
    name: 'Drywall Services',
    title: 'Drywall services',
    serviceId: 'drywall-repair',
    intro:
      'Drywall repair, installation, finishing and texturing for Las Vegas homes and businesses — from nail pops and holes to custom media walls and ceiling updates.',
    coverageIntro:
      'Drywall improvements add style and dimension to your home. We help you build your space around your lifestyle.',
    coverage: [
      { title: 'Drywall repair', text: 'Cracks, nail pops and holes from wear and tear, settling or accidents.' },
      { title: 'Drywall installation', text: 'New construction and renovations finished seamlessly.' },
      { title: 'Smooth finish', text: 'A sleek, clean surface ready for paint or wallpaper.' },
      { title: 'Texture styles', text: 'From subtle orange peel to dramatic knockdown.' },
      { title: 'Acoustic ceiling repair & removal', text: 'Popcorn and acoustic textures repaired or removed.' },
      { title: 'Custom features', text: 'Coffered ceilings, recessed niches and built-in media walls.' },
      { title: 'Dropped ceiling removal', text: 'Open up a room and give it the feel of more space.' },
      { title: 'Texture matching', text: 'New texture blended with existing walls for a consistent look.' },
      { title: 'Water-damaged drywall', text: 'Stained and remediated areas rebuilt and refinished.' },
    ],
    sections: [
      {
        title: 'Finishing that elevates your interiors',
        body: [
          'The finishing touches on your drywall have a big impact on the look and feel of your property. Our services go beyond basic installation and repair — whether you want a smooth, modern finish or a textured look with more character, our technicians deliver it.',
          'We pay close attention to every seam, joint and corner, so your walls and ceilings hold up to close inspection and daily life.',
        ],
      },
      {
        title: 'Texturing and refinishing',
        body: [
          'We can add artisan textures that complement your decor, refinish old or damaged textures to smooth out imperfections, and match new texture to what’s already there. A well-finished wall improves the look of your home and can add to its value.',
        ],
      },
      {
        title: 'Quality work that fits your budget',
        body: [
          'We use quality equipment and follow industry standards so your interiors look great and last. We also know budget matters, and we work to provide excellent drywall services without compromising on quality.',
          'Once your drywall is finished, we can also paint it so the new surface looks its best.',
        ],
      },
    ],
    checklist: {
      title: 'Your estimate walk-through',
      intro:
        'When you schedule an estimate, a member of our team walks your home with you to address your concerns. It takes anywhere from 15 minutes to an hour, depending on your needs. Common items we look at:',
      items: [
        { title: 'Cracking, nail pops or holes', text: 'From normal wear and tear, settling or accidental damage.' },
        { title: 'Peeling areas', text: 'Loose drywall tape and peeling paint.' },
        { title: 'Texture', text: 'Inconsistent texture, acoustic or popcorn ceilings, or tape and texture on new drywall.' },
        { title: 'Water damage', text: 'Stained drywall and areas that have been remediated due to water.' },
      ],
    },
    faqs: [
      {
        q: 'What is drywall made of?',
        a: 'Drywall is mainly gypsum, a naturally occurring mineral, sandwiched between two layers of paper. That makes it sturdy but lightweight — ideal for interior walls and ceilings.',
      },
      {
        q: 'How long does it take to install drywall?',
        a: 'It depends on the size of the area and how complex the project is. A professional team can typically install drywall in a standard room within a day or two.',
      },
      {
        q: 'What does drywall installation cost?',
        a: 'Cost depends on the size of the project, the type of drywall and the complexity of the job. Call us or request a free quote and we’ll give you a clear price for your project.',
      },
      {
        q: 'How do you repair drywall?',
        a: 'We cut out the damaged area, install a new piece of drywall, tape the seams, apply joint compound, sand it smooth and paint over the repair.',
      },
      {
        q: 'What’s the difference between drywall and plaster?',
        a: 'Both create walls and ceilings. Drywall is more modern and faster to install. Plaster is more traditional and gives a harder, more solid finish.',
      },
    ],
    related: [
      'drywall-installation-las-vegas',
      'texture-matching-las-vegas',
      'popcorn-ceiling-removal-las-vegas',
      'painting-services-las-vegas',
    ],
  },
  {
    slug: 'painting-services-las-vegas',
    name: 'Painting Services',
    title: 'Painting services',
    serviceId: 'painting',
    intro:
      'Interior and exterior painting for Las Vegas homes and businesses. A fresh coat of paint can give your home the look and feel you’ve always wanted — let us do the work for you.',
    coverageIntro: 'We handle projects of any size, from a single accent wall to an entire office building.',
    coverage: [
      { title: 'Interior painting', text: 'Single rooms, accent walls or the whole house.' },
      { title: 'Exterior painting', text: 'Durable products chosen to stand up to Las Vegas heat.' },
      { title: 'Gates & fences', text: 'Refresh the outdoor details that frame your home.' },
      { title: 'Drywall patching', text: 'We patch walls before painting so the finish looks right.' },
      { title: 'Wood staining', text: 'Stain for wood surfaces inside and out.' },
      { title: 'Cabinet, stair & door refinishing', text: 'Bring worn woodwork back to life.' },
      { title: 'Color upgrades', text: 'Help choosing colors that match your style.' },
      { title: 'Wallcovering & faux finish removal', text: 'Outdated finishes removed for a clean, modern look.' },
    ],
    sections: [
      {
        title: 'Transform your space',
        body: [
          'Whether you want a single room or accent wall painted, or a complete makeover, we can help. Instead of shopping for new furniture or decor, consider changing your paint color. Our painters make it easy and affordable to pick a color that matches your personality or showcases a favorite piece.',
          'We can also remove outdated decorative wall coverings and faux finishes to give your new paint a warm, contemporary look.',
        ],
      },
      {
        title: 'Exterior paint built for Las Vegas heat',
        body: [
          'The Las Vegas heat is brutal on exterior paint, causing fading and chipping that can make even a newer home look older than it is. We know which quality paint products are designed to endure extreme heat and resist drying out.',
        ],
      },
      {
        title: 'Rooted in Las Vegas',
        body: [
          'We’re deeply rooted in Las Vegas and proud to add to the city’s charm with quality work. For us, painting isn’t just about color — it’s about adding value and style to your property with attention to detail, good customer service and high-quality materials.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What types of properties do you service?',
        a: 'We paint both residential and commercial properties in Las Vegas.',
      },
      {
        q: 'Do you offer environmentally friendly paint and stain options?',
        a: 'Yes. We offer a range of eco-friendly paints and stains that are low in volatile organic compounds (VOCs) while still providing a durable, high-quality finish.',
      },
    ],
    related: [
      'drywall-services-las-vegas',
      'wallpaper-services-las-vegas',
      'stucco-services-las-vegas',
      'texture-matching-las-vegas',
    ],
  },
  {
    slug: 'carpentry-services-las-vegas',
    name: 'Carpentry Services',
    title: 'Carpentry services',
    intro:
      'Custom carpentry and woodwork for Las Vegas homes and businesses — from cabinets, stairs and built-ins to furniture repair and full renovations.',
    coverageIntro:
      'Every project is different, so we tailor our carpentry to your space, your style and how you use it.',
    coverage: [
      { title: 'Furniture design & creation', text: 'Custom pieces built for your style and needs.' },
      { title: 'Home & office renovation', text: 'Carpentry that refreshes the spaces you live and work in.' },
      { title: 'Large-scale joinery', text: 'Projects that balance good looks with everyday use.' },
      { title: 'Repair & maintenance', text: 'Keep treasured pieces in top condition.' },
      { title: 'Kitchen woodwork', text: 'Custom cabinets, countertops and storage solutions.' },
      { title: 'Bathroom woodwork', text: 'Cabinets, shelves and vanities that add storage and style.' },
      { title: 'Custom cabinets', text: 'For kitchens, bathrooms and offices.' },
      { title: 'Stairs', text: 'Well-crafted stairs that become a focal point.' },
      { title: 'Wood flooring', text: 'Hardwood and engineered wood floors.' },
      { title: 'Corners & tricky spaces', text: 'Smart solutions so every corner gets used.' },
      { title: 'Roof carpentry', text: 'Roof carpentry built to fit your home’s architecture.' },
    ],
    sections: [
      {
        title: 'Craftsmanship for every project',
        body: [
          'Whether you’re planning a large renovation or want to breathe new life into a cherished piece of furniture, finding the right carpenter matters. We focus on craftsmanship, attention to detail and customer satisfaction for both households and businesses.',
        ],
      },
      {
        title: 'Tradition meets modern technique',
        body: [
          'Our carpenters are skilled in both traditional woodworking and modern techniques. Combining the two lets us deliver work that is durable, well-made and unique to your home.',
        ],
      },
      {
        title: 'Kitchens, baths and beyond',
        body: [
          'Your kitchen is the heart of your home. We build custom cabinets, countertops and storage that blend function with style. In bathrooms, we add cabinets, shelves and vanities for more storage and a finished look.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What’s included in your carpentry services?',
        a: 'Custom furniture design and creation, home and office renovation, large-scale joinery projects, and furniture repair and maintenance.',
      },
      {
        q: 'How experienced are your carpenters?',
        a: 'Our carpenters have several years of experience across different areas of carpentry, with skills in both modern and traditional woodworking techniques.',
      },
      {
        q: 'What materials do you use?',
        a: 'We choose the wood based on each project, including pine, oak, walnut and more. Our materials are high quality and responsibly sourced.',
      },
    ],
    related: [
      'doors-trim-baseboards-las-vegas',
      'handyman-services-las-vegas',
      'flooring-installation-las-vegas',
      'painting-services-las-vegas',
    ],
  },
  {
    slug: 'stucco-services-las-vegas',
    name: 'Stucco Services',
    title: 'Stucco services',
    serviceId: 'stucco',
    intro:
      'Stucco repair, refinishing and new application for Las Vegas homes and properties. Stucco is the trade homeowners know the least about — we specialize in it so you don’t have to.',
    coverageIntro: 'From hairline cracks to full new applications, we bring the same care to every stucco job.',
    coverage: [
      { title: 'Stucco repairs', text: 'Cracks, holes, weathering and staining repaired to blend in.' },
      { title: 'Stucco pop-outs', text: 'Decorative pop-outs added or repaired.' },
      { title: 'New stucco application', text: 'Fresh stucco applied with the finish you choose.' },
      { title: 'Upgrading brick with stucco', text: 'Give brick surfaces a clean, updated stucco look.' },
      { title: 'Walls, patios & BBQ areas', text: 'Coating for perimeter and retaining walls, barbecue areas, patios and more.' },
      { title: 'Refinishing', text: 'Restore the look of faded or worn stucco.' },
    ],
    sections: [
      {
        title: 'How stucco works',
        body: [
          'Stucco is a two- or three-part coating applied over wood, masonry and, most often, lath. It’s naturally fire-resistant, durable and low maintenance. Common finishes include lace, Monterey or Santa Fe, flat and sand.',
          'When stucco isn’t applied properly, it can lead to water getting in, mold, rot and even structural damage — all of which mean costly repairs.',
        ],
      },
      {
        title: 'Why repairs shouldn’t wait',
        body: [
          'Stucco is a durable protective coating, but it can still let water in when damage isn’t addressed quickly. Hairline cracks are fairly easy to fill, but anything beyond that can point to other problems that need a skilled stucco contractor to repair correctly — and seamlessly, so the patch doesn’t show.',
          'We recommend checking the outside of your home regularly for areas that need attention.',
        ],
      },
      {
        title: 'Repairs, refinishing and installation',
        body: [
          'We address cracks, weathering and staining with precision, and handle new stucco installations from start to finish using quality tools and materials. Every project is different, and we adapt our approach to your property — with clear communication, respect for your space and a commitment to your timeline.',
        ],
      },
    ],
    checklist: {
      title: 'Free exterior walk-through',
      intro:
        'Home Repair MD can walk your home with you at no cost to look for potential hazards or wear. Common things we look for:',
      items: [
        { title: 'Hairline or settlement cracks', text: 'Caused by normal stresses, vibrations or shifting soil around the home.' },
        { title: 'Bubbling or blistering', text: 'Caused by ongoing water presence and efflorescence.' },
        { title: 'Indentations or soft spots', text: 'A sign of water damage hiding beneath the stucco.' },
        { title: 'Holes', text: 'Caused by wildlife or direct impact.' },
      ],
    },
    faqs: [
      {
        q: 'What are the benefits of using your stucco services?',
        a: 'Precise workmanship, competitive pricing and excellent customer service, so your project turns out the best it can.',
      },
    ],
    related: [
      'painting-services-las-vegas',
      'texture-matching-las-vegas',
      'water-damage-repair-las-vegas',
      'handyman-services-las-vegas',
    ],
  },
  {
    slug: 'wallpaper-services-las-vegas',
    name: 'Wallpaper Services',
    title: 'Wallpaper services',
    serviceId: 'wallpaper',
    intro:
      'Wallpaper installation, removal, custom designs and wall murals for Las Vegas homes and businesses. Bold statement or subtle pattern, we’ll help you get the look you want.',
    coverageIntro: 'Our wallpaper work goes beyond installation — from choosing a design to caring for it afterward.',
    coverage: [
      { title: 'Wallpaper installation', text: 'Smooth, seamless installs with patterns that line up at every corner.' },
      { title: 'Custom wallpaper', text: 'Abstract, floral or geometric designs tailored to your taste.' },
      { title: 'Wall murals', text: 'Art, photos and unique visuals turned into a feature wall.' },
      { title: 'Wallpaper removal', text: 'Old paper and residue removed, leaving walls ready for what’s next.' },
      { title: 'Design consultation', text: 'Help choosing a design based on lighting, room size and decor.' },
      { title: 'Residential & commercial', text: 'Homes, offices, restaurants, hotels and retail spaces.' },
      { title: 'Post-installation care', text: 'Care guidance and minor repairs after the job is done.' },
    ],
    sections: [
      {
        title: 'Installation done right',
        body: [
          'We focus on the details so each corner fits and the pattern stays consistent from wall to wall. The result is a smooth, seamless finish.',
        ],
      },
      {
        title: 'Help choosing the right design',
        body: [
          'With so many options, picking wallpaper can be overwhelming. We consider your room’s lighting, size and existing decor to recommend designs that suit your space and your style.',
        ],
      },
      {
        title: 'For homes and businesses',
        body: [
          'Revamp a living room, add a playful touch to a child’s room or bring a calm feel to your bedroom. For businesses, we create wallpaper solutions for offices, restaurants, hotels and retail stores that support your brand and make a statement.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What types of wallpaper do you offer?',
        a: 'A variety, including vinyl, fabric, foil, textured and custom designs, so you can find the right fit for your space.',
      },
      {
        q: 'How do I clean my wallpaper?',
        a: 'It depends on the type. Most wallpaper stays looking good with light dusting or a wipe with a damp cloth. We provide specific cleaning guidance for every wallpaper we install.',
      },
      {
        q: 'How do you make wallpaper installations and removals last?',
        a: 'We use high-quality materials and careful techniques so installations are durable and smooth. For removals, we take care to avoid damaging the surfaces underneath.',
      },
      {
        q: 'Is it worth having wallpaper removed by a professional?',
        a: 'Yes. A professional removes wallpaper without damaging the walls, uses the right tools and techniques, and can handle any repairs or surface prep needed for the next step.',
      },
    ],
    related: [
      'painting-services-las-vegas',
      'drywall-services-las-vegas',
      'texture-matching-las-vegas',
      'handyman-services-las-vegas',
    ],
  },
]
